import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faCalendarCheck,
  faFlask,
  faPhoneVolume,
  faCapsules,
  faUser,
  faCalendarDays,
  faClock,
  faCheck,
  faPen,
  faTrash,
  faPlus,
  faList,
  faHourglassHalf,
  faSun,
  faCircleCheck,
  faArrowsRotate,
  faRepeat,
  faCalendarDay,
  faCalendarWeek,
  faRotate,
  faFileLines,
  faTags,
  faExclamationTriangle,
  faListCheck,
  faStickyNote,
  faChevronDown,
  faChevronUp,
  faCircle,
  faSpinner,
  faMinus,
  faSyringe,
  faShieldAlt,
  faStethoscope,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import reminderService, {
  Reminder,
  ReminderPayload,
  SubTask,
} from '../../services/reminderService';
import taskService from '../../services/taskService';
import './Reminders.css';
import BackButton from '../../components/BackButton/BackButton';
import { useProfile } from '../../context/ProfileContext';

// ─── Types ────────────────────────────────────────────────────────────

type ReminderStatus = 'upcoming' | 'today' | 'overdue' | 'completed';
type TabKey = 'all' | 'upcoming' | 'today' | 'completed' | 'tasks' | 'add';

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'info';
}

interface FormSubtask {
  title: string;
  is_completed: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────

const CATEGORY_META: Record<string, { icon: IconDefinition; color: string }> = {
  medication: { icon: faCapsules, color: '#ef4444' },
  appointment: { icon: faCalendarCheck, color: '#3b82f6' },
  lab_test: { icon: faFlask, color: '#06b6d4' },
  follow_up: { icon: faPhoneVolume, color: '#f59e0b' },
  vaccination: { icon: faSyringe, color: '#8b5cf6' },
  insurance: { icon: faShieldAlt, color: '#ec4899' },
  health_checkup: { icon: faStethoscope, color: '#10b981' },
  personal: { icon: faUser, color: '#f43f5e' },
  task: { icon: faListCheck, color: '#6366f1' },
  other: { icon: faBell, color: '#64748b' },
};
const DEFAULT_CATEGORY_META = { icon: faBell, color: '#64748b' };

const RECURRENCE_META: Record<string, IconDefinition> = {
  one_time: faCalendarDay,
  daily: faArrowsRotate,
  weekly: faCalendarWeek,
  monthly: faRotate,
};

const TASK_STATUS_META: Record<string, { icon: IconDefinition; color: string; bg: string }> = {
  not_started: { icon: faCircle, color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.1)' },
  in_progress: { icon: faSpinner, color: '#6366f1', bg: 'rgba(99, 102, 241, 0.1)' },
  completed: { icon: faCircleCheck, color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },
};
const DEFAULT_TASK_STATUS_META = {
  icon: faCircle,
  color: '#94a3b8',
  bg: 'rgba(148, 163, 184, 0.1)',
};

const INITIAL_FORM = {
  title: '',
  description: '',
  reminder_datetime: '',
  category: 'appointment',
  priority: 'medium',
  recurrence: 'one_time',
  notes: '',
  task_status: 'not_started' as 'not_started' | 'in_progress' | 'completed',
  subtasks: [] as FormSubtask[],
};

const TAB_CONFIG: { key: TabKey; label: string; icon: IconDefinition; headerTitle: string; headerDesc: string; headerClass: string; iconClass: string }[] = [
  { key: 'all', label: 'All Reminders', icon: faList, headerTitle: 'All Reminders', headerDesc: 'Your complete list of health reminders. Tasks live in their own tab.', headerClass: 'rem-tab-header-all', iconClass: 'rem-icon-all' },
  { key: 'upcoming', label: 'Upcoming', icon: faHourglassHalf, headerTitle: 'Upcoming Reminders', headerDesc: 'Reminders scheduled for the future. Stay prepared.', headerClass: 'rem-tab-header-upcoming', iconClass: 'rem-icon-upcoming' },
  { key: 'today', label: "Today", icon: faSun, headerTitle: "Today's Reminders", headerDesc: 'Reminders due today and any overdue items.', headerClass: 'rem-tab-header-today', iconClass: 'rem-icon-today' },
  { key: 'completed', label: 'Completed', icon: faCircleCheck, headerTitle: 'Completed Reminders', headerDesc: 'Reminders you have successfully completed.', headerClass: 'rem-tab-header-completed', iconClass: 'rem-icon-completed' },
  { key: 'tasks', label: 'Tasks', icon: faListCheck, headerTitle: 'My Tasks', headerDesc: 'Track and manage your personal tasks and to-dos.', headerClass: 'rem-tab-header-tasks', iconClass: 'rem-icon-tasks' },
];

// ─── Helpers ──────────────────────────────────────────────────────────

const getStatus = (r: Reminder): ReminderStatus => {
  if (r.is_completed) return 'completed';
  const dt = new Date(r.reminder_datetime);
  const now = new Date();
  if (dt.toDateString() === now.toDateString()) return 'today';
  if (dt < now) return 'overdue';
  return 'upcoming';
};

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toDatetimeLocal = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const titleCase = (value: string): string =>
  value
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');

// ─── Toast Component ──────────────────────────────────────────────────

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const iconMap = { success: faCheck, error: faExclamationTriangle, info: faBell };

  return (
    <div className={`rem-toast rem-toast-${type}`}>
      <span className="rem-toast-icon">
        <FontAwesomeIcon icon={iconMap[type]} />
      </span>
      <span className="rem-toast-message">{message}</span>
      <button className="rem-toast-close" onClick={onClose} aria-label="Close notification">
        {'\u00D7'}
      </button>
    </div>
  );
};

// ─── Skeleton Loader ──────────────────────────────────────────────────

const SkeletonCards: React.FC = () => (
  <div className="rem-skeleton-grid">
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="rem-skeleton-card" style={{ position: 'relative' }}>
        <div className="rem-skeleton-badge" />
        <div className="rem-skeleton-header">
          <div className="rem-skeleton-circle" />
          <div className="rem-skeleton-header-lines">
            <div className="rem-skeleton-line medium" />
            <div className="rem-skeleton-line short" style={{ marginBottom: 0 }} />
          </div>
        </div>
        <div className="rem-skeleton-line long" />
        <div className="rem-skeleton-line medium" />
        <div className="rem-skeleton-line short" style={{ marginBottom: 0 }} />
      </div>
    ))}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────

const VALID_TAB_KEYS: TabKey[] = ['all', 'upcoming', 'today', 'completed', 'tasks', 'add'];

const Reminders: React.FC = () => {
  const { t } = useTranslation(['services', 'common']);
  const { activeProfile } = useProfile();
  const location = useLocation();
  const navigate = useNavigate();

  const initialTab: TabKey = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('tab') as TabKey | null;
    return raw && VALID_TAB_KEYS.includes(raw) ? raw : 'all';
  }, [location.search]);

  // State
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [choices, setChoices] = useState<{
    category: Record<string, string>;
    priority: Record<string, string>;
    recurrence: Record<string, string>;
    task_status: Record<string, string>;
  }>({
    category: {},
    priority: {},
    recurrence: {},
    task_status: {},
  });

  // Task-specific state
  const [expandedTasks, setExpandedTasks] = useState<Set<number>>(new Set());
  const [subtaskInputs, setSubtaskInputs] = useState<Record<number, string>>({});
  const [subtaskLoading, setSubtaskLoading] = useState<Set<number>>(new Set());
  const [formSubtaskInput, setFormSubtaskInput] = useState('');

  // Toast helper
  const showToast = useCallback((message: string, type: ToastState['type']) => {
    setToast({ message, type });
  }, []);

  const getDependantId = useCallback((): number | undefined => {
    if (activeProfile && activeProfile.type !== 'self' && typeof activeProfile.id === 'number') {
      return activeProfile.id;
    }
    return undefined;
  }, [activeProfile]);

  const fetchReminders = useCallback(async () => {
    try {
      setLoading(true);
      const [reminderData, taskData] = await Promise.all([
        reminderService.getReminders(getDependantId()),
        taskService.getTasks(),
      ]);
      setReminders([...reminderData, ...taskData]);
    } catch (err) {
      console.error('Failed to fetch reminders:', err);
      showToast(t('services:reminders.failedToLoadReminders'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, getDependantId, t]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders, activeProfile]);

  useEffect(() => {
    const loadChoices = async () => {
      try {
        const [reminderChoices, taskChoices] = await Promise.all([
          reminderService.getChoices(),
          taskService.getChoices(),
        ]);
        setChoices({
          category: reminderChoices.category || {},
          priority: reminderChoices.priority || taskChoices.priority || {},
          recurrence: reminderChoices.recurrence || taskChoices.recurrence || {},
          task_status: taskChoices.task_status || {},
        });
      } catch (err) {
        console.error('Failed to load reminder/task choices:', err);
      }
    };
    loadChoices();
  }, []);

  const categoryConfig = useMemo<
    Record<string, { icon: IconDefinition; label: string; color: string }>
  >(() => {
    const entries: Record<string, { icon: IconDefinition; label: string; color: string }> = {};
    Object.entries(choices.category).forEach(([value, label]) => {
      const meta = CATEGORY_META[value] || DEFAULT_CATEGORY_META;
      entries[value] = { icon: meta.icon, color: meta.color, label };
    });
    if (!entries.task) {
      const meta = CATEGORY_META.task || DEFAULT_CATEGORY_META;
      entries.task = { icon: meta.icon, color: meta.color, label: 'Task' };
    }
    return entries;
  }, [choices.category]);

  const priorityOptions = useMemo(
    () => Object.entries(choices.priority).map(([value, label]) => ({ value, label })),
    [choices.priority]
  );

  const recurrenceOptions = useMemo(
    () =>
      Object.entries(choices.recurrence).map(([value, label]) => ({
        value,
        label,
        icon: RECURRENCE_META[value] || faCalendarDay,
      })),
    [choices.recurrence]
  );

  const taskStatusOptions = useMemo(
    () =>
      Object.entries(choices.task_status).map(([value, label]) => {
        const meta = TASK_STATUS_META[value] || DEFAULT_TASK_STATUS_META;
        return { value, label, icon: meta.icon, color: meta.color, bg: meta.bg };
      }),
    [choices.task_status]
  );

  const getTaskStatusConfig = useCallback(
    (status: string) => {
      const meta = TASK_STATUS_META[status] || DEFAULT_TASK_STATUS_META;
      const label = choices.task_status[status] || titleCase(status || 'not_started');
      return { label, color: meta.color, bg: meta.bg };
    },
    [choices.task_status]
  );

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  // Re-fetch when a reminder is added via QuickAddFAB
  useEffect(() => {
    const handler = () => fetchReminders();
    window.addEventListener('reminders-updated', handler);
    return () => window.removeEventListener('reminders-updated', handler);
  }, [fetchReminders]);

  const stats = useMemo(() => {
    let totalReminders = 0;
    let upcoming = 0;
    let today = 0;
    let completed = 0;
    let tasks = 0;
    reminders.forEach((r) => {
      if (r.category === 'task') {
        tasks++;
        return;
      }
      totalReminders++;
      const s = getStatus(r);
      if (s === 'upcoming') upcoming++;
      else if (s === 'today' || s === 'overdue') today++;
      else if (s === 'completed') completed++;
    });
    return { totalReminders, upcoming, today, completed, tasks };
  }, [reminders]);

  const filteredReminders = useMemo(() => {
    if (activeTab === 'add') return [];
    if (activeTab === 'tasks') return reminders.filter((r) => r.category === 'task');
    const remindersOnly = reminders.filter((r) => r.category !== 'task');
    if (activeTab === 'all') return remindersOnly;
    return remindersOnly.filter((r) => {
      const s = getStatus(r);
      if (activeTab === 'upcoming') return s === 'upcoming';
      if (activeTab === 'today') return s === 'today' || s === 'overdue';
      if (activeTab === 'completed') return s === 'completed';
      return true;
    });
  }, [activeTab, reminders]);

  // Form handlers
  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM);
    setEditingId(null);
    setFormSubtaskInput('');
  }, []);

  const handleFieldChange = (
    field: keyof typeof INITIAL_FORM,
    value: string | FormSubtask[]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditReminder = (reminder: Reminder) => {
    setFormData({
      title: reminder.title,
      description: reminder.description || '',
      reminder_datetime: toDatetimeLocal(reminder.reminder_datetime),
      category: reminder.category || 'appointment',
      priority: reminder.priority || 'medium',
      recurrence: reminder.recurrence || 'one_time',
      notes: reminder.notes || '',
      task_status: reminder.task_status || 'not_started',
      subtasks: (reminder.subtasks || []).map((st) => ({
        title: st.title,
        is_completed: st.is_completed,
      })),
    });
    setFormSubtaskInput('');
    setEditingId(reminder.id);
    setActiveTab('add');
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      showToast(t('services:reminders.enterTitle'), 'error');
      return;
    }
    if (!formData.reminder_datetime) {
      showToast(t('services:reminders.selectDateTime'), 'error');
      return;
    }

    try {
      setSaving(true);
      const payload: ReminderPayload = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        reminder_datetime: new Date(formData.reminder_datetime).toISOString(),
        category: formData.category,
        priority: formData.priority,
        recurrence: formData.recurrence,
        notes: formData.notes.trim() || undefined,
      };

      const isTask = formData.category === 'task';
      if (isTask) {
        payload.task_status = formData.task_status;
        payload.subtasks = formData.subtasks.map((st, idx) => ({
          title: st.title,
          is_completed: st.is_completed,
          order: idx,
        }));
      }

      if (editingId !== null) {
        if (isTask) {
          await taskService.updateTask(editingId, payload);
        } else {
          await reminderService.updateReminder(editingId, payload);
        }
        showToast(isTask ? t('services:reminders.taskUpdated') : t('services:reminders.reminderUpdated'), 'success');
      } else {
        if (isTask) {
          await taskService.createTask(payload);
        } else {
          await reminderService.createReminder(payload);
        }
        showToast(isTask ? t('services:reminders.taskCreated') : t('services:reminders.reminderCreated'), 'success');
      }

      resetForm();
      fetchReminders();
      setActiveTab(isTask ? 'tasks' : 'all');
    } catch (err) {
      console.error('Error saving reminder:', err);
      showToast(t('services:reminders.failedToSave'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const isTaskCategory = (id: number) =>
    reminders.find((r) => r.id === id)?.category === 'task';

  const handleDelete = async (id: number) => {
    try {
      const isTask = isTaskCategory(id);
      if (isTask) {
        await taskService.deleteTask(id);
      } else {
        await reminderService.deleteReminder(id);
      }
      setReminders((prev) => prev.filter((r) => r.id !== id));
      showToast(
        t(isTask
          ? 'services:reminders.taskDeleted'
          : 'services:reminders.reminderDeleted'),
        'success',
      );
      setConfirmDelete(null);
    } catch (err) {
      console.error('Error deleting reminder:', err);
      showToast(t('services:reminders.failedToDelete'), 'error');
    }
  };

  const handleMarkComplete = async (id: number) => {
    try {
      const isTask = isTaskCategory(id);
      const updated = isTask
        ? await taskService.markComplete(id)
        : await reminderService.markComplete(id);
      setReminders((prev) =>
        prev.map((r) => (r.id === id ? { ...r, ...updated, is_completed: true } : r))
      );
      showToast(
        t(isTask
          ? 'services:reminders.taskMarkedComplete'
          : 'services:reminders.reminderMarkedComplete'),
        'success',
      );
    } catch (err) {
      console.error('Error marking complete:', err);
      showToast(t('services:reminders.failedToComplete'), 'error');
    }
  };

  // ─── Subtask handlers (inline, for task list view) ──────────────────

  const toggleTaskExpanded = (taskId: number) => {
    setExpandedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const handleAddSubtaskInline = async (reminderId: number) => {
    const title = (subtaskInputs[reminderId] || '').trim();
    if (!title) return;

    setSubtaskLoading((prev) => new Set(prev).add(reminderId));
    try {
      const updated = await taskService.addSubtask(reminderId, title);
      setReminders((prev) => prev.map((r) => (r.id === reminderId ? { ...r, ...updated } : r)));
      setSubtaskInputs((prev) => ({ ...prev, [reminderId]: '' }));
    } catch (err) {
      console.error('Error adding subtask:', err);
      showToast(t('services:reminders.failedToAddSubtask'), 'error');
    } finally {
      setSubtaskLoading((prev) => {
        const next = new Set(prev);
        next.delete(reminderId);
        return next;
      });
    }
  };

  const handleToggleSubtask = async (reminderId: number, subtaskId: number) => {
    try {
      const updated = await taskService.toggleSubtask(reminderId, subtaskId);
      setReminders((prev) => prev.map((r) => (r.id === reminderId ? { ...r, ...updated } : r)));
    } catch (err) {
      console.error('Error toggling subtask:', err);
      showToast(t('services:reminders.failedToUpdateSubtask'), 'error');
    }
  };

  const handleDeleteSubtaskInline = async (reminderId: number, subtaskId: number) => {
    try {
      const updated = await taskService.deleteSubtask(reminderId, subtaskId);
      setReminders((prev) => prev.map((r) => (r.id === reminderId ? { ...r, ...updated } : r)));
    } catch (err) {
      console.error('Error deleting subtask:', err);
      showToast(t('services:reminders.failedToDeleteSubtask'), 'error');
    }
  };

  // ─── Form subtask handlers ─────────────────────────────────────────

  const handleAddFormSubtask = () => {
    const title = formSubtaskInput.trim();
    if (!title) return;
    setFormData((prev) => ({
      ...prev,
      subtasks: [...prev.subtasks, { title, is_completed: false }],
    }));
    setFormSubtaskInput('');
  };

  const handleRemoveFormSubtask = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      subtasks: prev.subtasks.filter((_, i) => i !== index),
    }));
  };

  const ADD_TAB_FALLBACK = {
    icon: faPlus,
    headerClass: 'rem-tab-header-add',
    iconClass: 'rem-icon-add',
    headerTitle: '',
    headerDesc: '',
  };
  const activeTabConfig =
    TAB_CONFIG.find((t) => t.key === activeTab) ?? ADD_TAB_FALLBACK;

  // Dynamic header for add/edit
  const headerTitle = activeTab === 'add'
    ? (editingId !== null
      ? (formData.category === 'task' ? 'Edit Task' : 'Edit Reminder')
      : (formData.category === 'task' ? 'New Task' : 'New Reminder'))
    : activeTabConfig.headerTitle;

  const headerDesc = activeTab === 'add'
    ? (editingId !== null
      ? (formData.category === 'task' ? 'Update the details of this task.' : 'Update the details of this reminder.')
      : (formData.category === 'task' ? 'Fill in the details to create a new task.' : 'Fill in the details below to create a new health reminder.'))
    : activeTabConfig.headerDesc;

  // ─── Render: Cards ──────────────────────────────────────────────────

  const renderCards = () => {
    if (loading) return <SkeletonCards />;

    if (filteredReminders.length === 0) {
      return (
        <div className="rem-empty-state">
          <div className="rem-empty-icon-wrap">
            <FontAwesomeIcon icon={faBell} />
          </div>
          <h3>
            No {activeTab === 'all' ? '' : activeTab + ' '}reminders yet
          </h3>
          <p>
            {activeTab === 'all'
              ? 'Start organizing your health by adding your first reminder.'
              : `There are no ${activeTab} reminders to display right now.`}
          </p>
          <button
            className="rem-btn rem-btn-primary"
            onClick={() => { resetForm(); setActiveTab('add'); }}
          >
            <FontAwesomeIcon icon={faPlus} />
            {t('services:reminders.addFirstReminder')}
          </button>
        </div>
      );
    }

    return (
      <div className="rem-cards-grid">
        {filteredReminders.map((reminder, idx) => {
          const status = getStatus(reminder);
          const catConfig =
            categoryConfig[reminder.category] ||
            categoryConfig.other ||
            { icon: faBell, label: titleCase(reminder.category || 'other'), color: '#64748b' };
          const priorityClass = `rem-card-priority-${reminder.priority || 'medium'}`;

          return (
            <div
              key={reminder.id}
              className={`rem-card ${priorityClass}`}
              style={{ animationDelay: `${idx * 0.06}s` }}
            >
              {/* Status badge */}
              <div className={`rem-status-badge rem-status-${status}`}>
                {status === 'upcoming' && <><FontAwesomeIcon icon={faClock} /> Upcoming</>}
                {status === 'today' && <><FontAwesomeIcon icon={faSun} /> Today</>}
                {status === 'overdue' && <><FontAwesomeIcon icon={faExclamationTriangle} /> Overdue</>}
                {status === 'completed' && <><FontAwesomeIcon icon={faCheck} /> Completed</>}
              </div>

              <div className="rem-card-body">
                {/* Header: icon + title */}
                <div className="rem-card-header">
                  <div
                    className={`rem-category-icon-wrap rem-cat-${reminder.category || 'other'}`}
                  >
                    <FontAwesomeIcon icon={catConfig.icon} />
                  </div>
                  <div className="rem-card-title-area">
                    <h3 className="rem-card-title">{reminder.title}</h3>
                    {reminder.description && (
                      <p className="rem-card-desc">{reminder.description}</p>
                    )}
                  </div>
                </div>

                {/* Meta row */}
                <div className="rem-card-meta">
                  <span className="rem-card-datetime">
                    <FontAwesomeIcon icon={faCalendarDays} />
                    {formatDateTime(reminder.reminder_datetime)}
                  </span>
                  <span className={`rem-meta-badge rem-priority-${reminder.priority || 'medium'}`}>
                    {choices.priority[reminder.priority] || titleCase(reminder.priority || 'medium')}
                  </span>
                  {reminder.recurrence && reminder.recurrence !== 'one_time' && (
                    <span className="rem-meta-badge rem-recurrence-badge">
                      <FontAwesomeIcon icon={faRepeat} />
                      {choices.recurrence[reminder.recurrence] || titleCase(reminder.recurrence)}
                    </span>
                  )}
                </div>
              </div>

              {/* Action buttons */}
              <div className="rem-card-actions">
                {status !== 'completed' && (
                  <button
                    className="rem-action-btn rem-action-complete"
                    onClick={() => handleMarkComplete(reminder.id)}
                    title="Mark as complete"
                  >
                    <FontAwesomeIcon icon={faCheck} /> Complete
                  </button>
                )}
                <button
                  className="rem-action-btn rem-action-edit"
                  onClick={() => handleEditReminder(reminder)}
                  title="Edit reminder"
                >
                  <FontAwesomeIcon icon={faPen} /> Edit
                </button>
                <button
                  className="rem-action-btn rem-action-delete"
                  onClick={() => setConfirmDelete(reminder.id)}
                  title="Delete reminder"
                >
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ─── Render: Task List ──────────────────────────────────────────────

  const renderTaskList = () => {
    if (loading) return <SkeletonCards />;

    if (filteredReminders.length === 0) {
      return (
        <div className="rem-empty-state">
          <div className="rem-empty-icon-wrap">
            <FontAwesomeIcon icon={faListCheck} />
          </div>
          <h3>{t('services:reminders.noTasksYet')}</h3>
          <p>{t('services:reminders.noTasksDesc')}</p>
          <button
            className="rem-btn rem-btn-primary"
            onClick={() => {
              resetForm();
              setFormData(prev => ({ ...prev, category: 'task' }));
              setActiveTab('add');
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
            {t('services:reminders.addFirstTask')}
          </button>
        </div>
      );
    }

    return (
      <div className="rem-task-list-v2">
        {filteredReminders.map((task, idx) => {
          const status = getStatus(task);
          const isCompleted = status === 'completed';
          const isExpanded = expandedTasks.has(task.id);
          const taskStatusCfg = getTaskStatusConfig(task.task_status);
          const progress = task.subtask_progress || { completed: 0, total: 0 };
          const progressPercent = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;
          const subtasks = task.subtasks || [];
          const hasSubtasks = subtasks.length > 0;

          return (
            <div
              key={task.id}
              className={`rem-task-card-v2 ${isCompleted ? 'rem-task-card-v2--completed' : ''} rem-task-card-v2--${task.priority || 'medium'}`}
              style={{ animationDelay: `${idx * 0.05}s` }}
            >
              {/* Main task row */}
              <div className="rem-task-card-v2__main">
                <button
                  className={`rem-task-card-v2__checkbox ${isCompleted ? 'rem-task-card-v2__checkbox--checked' : ''}`}
                  onClick={() => !isCompleted && handleMarkComplete(task.id)}
                  title={isCompleted ? 'Completed' : 'Mark as complete'}
                  disabled={isCompleted}
                  aria-label={isCompleted ? 'Task completed' : 'Mark task as complete'}
                >
                  {isCompleted && <FontAwesomeIcon icon={faCheck} />}
                </button>

                <div className="rem-task-card-v2__body">
                  <div className="rem-task-card-v2__top-row">
                    <h3 className={`rem-task-card-v2__title ${isCompleted ? 'rem-task-card-v2__title--done' : ''}`}>
                      {task.title}
                    </h3>
                    <div className="rem-task-card-v2__badges">
                      <span
                        className="rem-task-card-v2__status-badge"
                        style={{ background: taskStatusCfg.bg, color: taskStatusCfg.color }}
                      >
                        <span className="rem-task-card-v2__status-dot" style={{ background: taskStatusCfg.color }} />
                        {taskStatusCfg.label}
                      </span>
                      <span className={`rem-meta-badge rem-priority-${task.priority || 'medium'}`}>
                        {choices.priority[task.priority] || titleCase(task.priority || 'medium')}
                      </span>
                    </div>
                  </div>

                  {task.description && (
                    <p className="rem-task-card-v2__desc">{task.description}</p>
                  )}

                  <div className="rem-task-card-v2__meta">
                    <span className="rem-task-card-v2__datetime">
                      <FontAwesomeIcon icon={faCalendarDays} />
                      {formatDateTime(task.reminder_datetime)}
                    </span>
                    {hasSubtasks && (
                      <span className="rem-task-card-v2__subtask-count">
                        <FontAwesomeIcon icon={faListCheck} />
                        {progress.completed}/{progress.total} subtasks
                      </span>
                    )}
                  </div>

                  {/* Subtask progress bar */}
                  {hasSubtasks && (
                    <div className="rem-task-card-v2__progress-wrap">
                      <div className="rem-task-card-v2__progress-bar">
                        <div
                          className="rem-task-card-v2__progress-fill"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                      <span className="rem-task-card-v2__progress-label">{progressPercent}%</span>
                    </div>
                  )}
                </div>

                <div className="rem-task-card-v2__actions">
                  {hasSubtasks && (
                    <button
                      className="rem-task-card-v2__expand-btn"
                      onClick={() => toggleTaskExpanded(task.id)}
                      title={isExpanded ? 'Collapse subtasks' : 'Expand subtasks'}
                      aria-label={isExpanded ? 'Collapse subtasks' : 'Expand subtasks'}
                    >
                      <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                    </button>
                  )}
                  {!hasSubtasks && (
                    <button
                      className="rem-task-card-v2__expand-btn"
                      onClick={() => toggleTaskExpanded(task.id)}
                      title="Add subtasks"
                      aria-label="Add subtasks"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  )}
                  <button
                    className="rem-action-btn rem-action-edit"
                    onClick={() => handleEditReminder(task)}
                    title="Edit task"
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                  <button
                    className="rem-action-btn rem-action-delete"
                    onClick={() => setConfirmDelete(task.id)}
                    title="Delete task"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>

              {/* Expandable subtask section */}
              {isExpanded && (
                <div className="rem-task-card-v2__subtasks">
                  {subtasks.length > 0 && (
                    <ul className="rem-task-card-v2__subtask-list">
                      {subtasks.map((st: SubTask) => (
                        <li key={st.id} className={`rem-task-card-v2__subtask-item ${st.is_completed ? 'rem-task-card-v2__subtask-item--done' : ''}`}>
                          <button
                            className={`rem-task-card-v2__subtask-check ${st.is_completed ? 'rem-task-card-v2__subtask-check--checked' : ''}`}
                            onClick={() => handleToggleSubtask(task.id, st.id)}
                            aria-label={st.is_completed ? 'Uncheck subtask' : 'Check subtask'}
                          >
                            {st.is_completed && <FontAwesomeIcon icon={faCheck} />}
                          </button>
                          <span className={`rem-task-card-v2__subtask-title ${st.is_completed ? 'rem-task-card-v2__subtask-title--done' : ''}`}>
                            {st.title}
                          </span>
                          <button
                            className="rem-task-card-v2__subtask-delete"
                            onClick={() => handleDeleteSubtaskInline(task.id, st.id)}
                            title="Remove subtask"
                            aria-label="Remove subtask"
                          >
                            <FontAwesomeIcon icon={faMinus} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Add subtask inline input */}
                  <div className="rem-task-card-v2__add-subtask">
                    <input
                      type="text"
                      className="rem-task-card-v2__add-subtask-input"
                      placeholder="Add a subtask..."
                      value={subtaskInputs[task.id] || ''}
                      onChange={(e) => setSubtaskInputs((prev) => ({ ...prev, [task.id]: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddSubtaskInline(task.id);
                        }
                      }}
                      disabled={subtaskLoading.has(task.id)}
                    />
                    <button
                      className="rem-task-card-v2__add-subtask-btn"
                      onClick={() => handleAddSubtaskInline(task.id)}
                      disabled={subtaskLoading.has(task.id) || !(subtaskInputs[task.id] || '').trim()}
                      aria-label="Add subtask"
                    >
                      {subtaskLoading.has(task.id) ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <FontAwesomeIcon icon={faPlus} />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ─── Render: Form ───────────────────────────────────────────────────

  const renderForm = () => (
    <div className="rem-form-section">
      <div className="rem-form-columns">
        {/* Left Column - Core fields */}
        <div className="rem-form-panel">
          <div className="rem-form-panel-title">
            <FontAwesomeIcon icon={faFileLines} />
            {formData.category === 'task' ? 'Task Details' : 'Reminder Details'}
          </div>

          <div className="rem-form-group">
            <label className="rem-form-label">
              Title <span className="rem-required">*</span>
            </label>
            <input
              type="text"
              className="rem-form-input"
              placeholder={formData.category === 'task' ? "e.g. Complete report, Submit assignment..." : "e.g. Doctor Appointment, Blood Test..."}
              value={formData.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
            />
          </div>

          <div className="rem-form-group">
            <label className="rem-form-label">Description</label>
            <textarea
              className="rem-form-textarea"
              placeholder="Brief description of this reminder..."
              rows={3}
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
            />
          </div>

          <div className="rem-form-group">
            <label className="rem-form-label">
              {formData.category === 'task' ? 'Due Date & Time' : 'Date & Time'} <span className="rem-required">*</span>
            </label>
            <input
              type="datetime-local"
              className="rem-form-input"
              value={formData.reminder_datetime}
              onChange={(e) => handleFieldChange('reminder_datetime', e.target.value)}
            />
          </div>

          <div className="rem-form-group">
            <label className="rem-form-label">
              <FontAwesomeIcon icon={faStickyNote} style={{ marginRight: 6, opacity: 0.5 }} />
              Notes
            </label>
            <textarea
              className="rem-form-textarea"
              placeholder="Any additional notes or instructions..."
              rows={3}
              value={formData.notes}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
            />
          </div>

          {/* Task-specific: Subtasks Builder */}
          {formData.category === 'task' && (
            <div className="rem-form-group">
              <label className="rem-form-label">
                <FontAwesomeIcon icon={faListCheck} style={{ marginRight: 6, opacity: 0.5 }} />
                Subtasks
              </label>
              <div className="rem-form-subtasks-builder">
                {formData.subtasks.length > 0 && (
                  <ul className="rem-form-subtask-list">
                    {formData.subtasks.map((st, idx) => (
                      <li key={idx} className="rem-form-subtask-item">
                        <span className="rem-form-subtask-bullet" />
                        <span className="rem-form-subtask-text">{st.title}</span>
                        <button
                          type="button"
                          className="rem-form-subtask-remove"
                          onClick={() => handleRemoveFormSubtask(idx)}
                          title="Remove subtask"
                          aria-label="Remove subtask"
                        >
                          {'\u00D7'}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="rem-form-subtask-add-row">
                  <input
                    type="text"
                    className="rem-form-input rem-form-subtask-input"
                    placeholder="Type a subtask and press Enter..."
                    value={formSubtaskInput}
                    onChange={(e) => setFormSubtaskInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddFormSubtask();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="rem-form-subtask-add-btn"
                    onClick={handleAddFormSubtask}
                    disabled={!formSubtaskInput.trim()}
                    aria-label="Add subtask"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Category, Priority, Recurrence */}
        <div className="rem-form-panel">
          <div className="rem-form-panel-title">
            <FontAwesomeIcon icon={faTags} />
            Classification
          </div>

          {/* Category Selector (hidden for tasks) */}
          {formData.category !== 'task' && (
            <div className="rem-form-group">
              <label className="rem-form-label">Category</label>
              <div className="rem-category-grid">
                {Object.entries(categoryConfig)
                  .filter(([key]) => key !== 'task')
                  .map(([key, cfg]) => (
                  <button
                    key={key}
                    type="button"
                    className={`rem-category-option cat-${key} ${formData.category === key ? 'selected' : ''}`}
                    onClick={() => handleFieldChange('category', key)}
                  >
                    <div className="rem-category-icon-inner" style={{ color: formData.category === key ? '#fff' : cfg.color, background: formData.category === key ? cfg.color : `${cfg.color}12` }}>
                      <FontAwesomeIcon icon={cfg.icon} />
                    </div>
                    {cfg.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Task Status (only for tasks) */}
          {formData.category === 'task' && (
            <div className="rem-form-group">
              <label className="rem-form-label">Task Status</label>
              <div className="rem-task-status-options">
                {taskStatusOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`rem-task-status-pill ${formData.task_status === opt.value ? 'rem-task-status-pill--active' : ''}`}
                    style={{
                      '--pill-color': opt.color,
                      '--pill-bg': `${opt.color}12`,
                      '--pill-bg-active': `${opt.color}18`,
                    } as React.CSSProperties}
                    onClick={() => handleFieldChange('task_status', opt.value)}
                  >
                    <FontAwesomeIcon icon={opt.icon} />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Priority */}
          <div className="rem-form-group">
            <label className="rem-form-label">Priority</label>
            <div className="rem-priority-options">
              {priorityOptions.map((p) => (
                <div key={p.value} className="rem-priority-radio">
                  <input
                    type="radio"
                    id={`priority-${p.value}`}
                    name="priority"
                    value={p.value}
                    checked={formData.priority === p.value}
                    onChange={() => handleFieldChange('priority', p.value)}
                  />
                  <label
                    htmlFor={`priority-${p.value}`}
                    className={`rem-priority-label rem-priority-label-${p.value}`}
                  >
                    <span className={`rem-priority-dot rem-dot-${p.value}`} />
                    {p.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Recurrence */}
          <div className="rem-form-group">
            <label className="rem-form-label">Recurrence</label>
            <div className="rem-recurrence-options">
              {recurrenceOptions.map((opt) => (
                <div key={opt.value} className="rem-recurrence-radio">
                  <input
                    type="radio"
                    id={`recurrence-${opt.value}`}
                    name="recurrence"
                    value={opt.value}
                    checked={formData.recurrence === opt.value}
                    onChange={() => handleFieldChange('recurrence', opt.value)}
                  />
                  <label
                    htmlFor={`recurrence-${opt.value}`}
                    className="rem-recurrence-label"
                  >
                    <FontAwesomeIcon icon={opt.icon} />
                    {opt.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Form action buttons */}
      <div className="rem-form-actions">
        <button
          type="button"
          className="rem-btn rem-btn-cancel"
          onClick={() => { resetForm(); setActiveTab('all'); }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="rem-btn rem-btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>Saving...</>
          ) : editingId !== null ? (
            <><FontAwesomeIcon icon={faCheck} /> {formData.category === 'task' ? 'Update Task' : 'Update Reminder'}</>
          ) : (
            <><FontAwesomeIcon icon={faPlus} /> {formData.category === 'task' ? 'Save Task' : 'Save Reminder'}</>
          )}
        </button>
      </div>
    </div>
  );

  // ─── Main Render ────────────────────────────────────────────────────

  return (
    <div className="rem-page">
      {/* Toast Notification */}
      {toast && (
        <div className="rem-toast-container">
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete !== null && (
        <div className="rem-modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="rem-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rem-confirm-icon">
              <FontAwesomeIcon icon={faTrash} />
            </div>
            <h3>Delete Reminder?</h3>
            <p>
              Are you sure you want to delete this reminder? This action cannot be undone.
            </p>
            <div className="rem-confirm-actions">
              <button
                className="rem-btn rem-btn-cancel"
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
              <button
                className="rem-btn rem-btn-danger"
                onClick={() => handleDelete(confirmDelete)}
              >
                <FontAwesomeIcon icon={faTrash} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WELCOME BANNER */}
      <section className="rem-welcome-banner">
        <Container>
          <BackButton />
          <div className="rem-welcome-layout">
            <div className="rem-welcome-text">
              <h1 className="rem-welcome-title">
                <FontAwesomeIcon icon={faBell} />
                Reminders & Tasks
              </h1>
              <p className="rem-welcome-subtitle">
                Stay on top of your health and to-dos. Manage appointments, tests, follow-ups, and personal tasks all in one place.
              </p>
            </div>
            <div className="rem-welcome-actions">
              <button
                className="rem-welcome-btn primary"
                onClick={() => {
                  resetForm();
                  setFormData(prev => ({ ...prev, category: 'appointment' }));
                  setActiveTab('add');
                }}
              >
                <FontAwesomeIcon icon={faPlus} />
                Add Reminder
              </button>
              <button
                className="rem-welcome-btn primary"
                onClick={() => {
                  resetForm();
                  setFormData(prev => ({ ...prev, category: 'task' }));
                  setActiveTab('add');
                }}
              >
                <FontAwesomeIcon icon={faPlus} />
                Add Task
              </button>
            </div>
          </div>
        </Container>
      </section>

      {/* STATS ROW */}
      <section className="rem-stats-section">
        <Container>
          <div className="rem-stats-row">
            {([
              { icon: faList, color: '#06b6d4', value: stats.totalReminders, label: 'Reminders' },
              { icon: faHourglassHalf, color: '#06b6d4', value: stats.upcoming, label: 'Upcoming' },
              { icon: faSun, color: '#f59e0b', value: stats.today, label: 'Today' },
              { icon: faCircleCheck, color: '#10b981', value: stats.completed, label: 'Completed' },
              { icon: faListCheck, color: '#6366f1', value: stats.tasks, label: 'Tasks' },
            ] as { icon: IconDefinition; color: string; value: number; label: string }[]).map((item, idx) => (
              <div className="rem-stat-card" key={idx}>
                <div className="rem-stat-icon" style={{ background: `${item.color}12`, color: item.color }}>
                  <FontAwesomeIcon icon={item.icon} />
                </div>
                <div className="rem-stat-info">
                  <span className="rem-stat-value">{item.value}</span>
                  <span className="rem-stat-label">{item.label}</span>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* TAB CONTENT */}
      <Container>
        <div className="rem-tab-content">

          {/* Tab Section Header with embedded navigation */}
          <div className={`rem-tab-header ${activeTabConfig.headerClass}`}>
            <div className="rem-tab-header-top">
              <div className="rem-tab-header-text">
                <h2>
                  <FontAwesomeIcon icon={activeTabConfig.icon} className={`rem-tab-header-icon ${activeTabConfig.iconClass}`} />
                  {headerTitle}
                </h2>
                <p>{headerDesc}</p>
              </div>
              {activeTab !== 'add' && (
                <button
                  className="rem-welcome-btn primary"
                  onClick={() => {
                    resetForm();
                    setFormData(prev => ({
                      ...prev,
                      category: activeTab === 'tasks' ? 'task' : 'appointment',
                    }));
                    setActiveTab('add');
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  {activeTab === 'tasks' ? 'Add Task' : 'Add Reminder'}
                </button>
              )}
            </div>
            <div className="rem-tabs-container" role="tablist">
              {TAB_CONFIG.map((tab) => (
                <button
                  key={tab.key}
                  className={`rem-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={() => {
                    if (tab.key === 'add') resetForm();
                    setActiveTab(tab.key);
                    navigate(`/reminders?tab=${tab.key}`, { replace: true });
                  }}
                  role="tab"
                  aria-selected={activeTab === tab.key}
                >
                  <span className="rem-tab-icon">
                    <FontAwesomeIcon icon={tab.icon} />
                  </span>
                  {tab.label}
                  {tab.key !== 'add' && (
                    <span className="rem-tab-count">
                      {tab.key === 'all' ? stats.totalReminders
                        : tab.key === 'upcoming' ? stats.upcoming
                        : tab.key === 'today' ? stats.today
                        : tab.key === 'completed' ? stats.completed
                        : tab.key === 'tasks' ? stats.tasks
                        : 0}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Card list, task list, or form */}
          {activeTab === 'add' ? renderForm() : activeTab === 'tasks' ? renderTaskList() : renderCards()}
        </div>
      </Container>
    </div>
  );
};

export default Reminders;
